import React, { useState } from "react";
import "./Pricing.css";
import { Briefcase, Users, Crown } from "lucide-react";
import Trial from "./Trial";

export default function Pricing() {
    const [selectedRegion, setSelectedRegion] = useState("India");
    const [rentalPlan, setRentalPlan] = useState("Single User");
    const [rentalMonths, setRentalMonths] = useState(1);

    // Base prices
    const indiaBase = 36000;
    const southAsiaBase = 200;
    const globalBase = 400;

    const pricingData = {
        regions: ["India", "South Asia", "Global"],
        plans: [
            {
                name: "Single User",
                icon: <Briefcase className="w-6 h-6 text-indigo-500" />,
                indiaPrice: indiaBase,
                southAsiaPrice: southAsiaBase,
                globalPrice: globalBase,
                discount: 0.2, // 20% off
                features: [
                    "1 User Account",
                    "Basic Billing",
                    "Basic View Report",
                    "Perfect for individuals needing a simple solution with essential features.",
                ],
            },
            {
                name: "Multi User",
                icon: <Crown className="w-6 h-6 text-yellow-500" />,
                indiaPrice: indiaBase * 3,
                southAsiaPrice: southAsiaBase * 3,
                globalPrice: globalBase * 3,
                features: [
                    "Unlimited Users",
                    "Full Analytics",
                    "Dedicated Manager",
                    "Custom Integrations",
                    "Fast Billing with Multiple POS Options",
                    "Includes all features of other plans",
                ],
                popular: true,
            },
            {
                name: "Dual User",
                icon: <Users className="w-6 h-6 text-blue-500" />,
                indiaPrice: indiaBase * 1.75,
                southAsiaPrice: southAsiaBase * 1.75,
                globalPrice: globalBase * 1.75,
                features: [
                    "2 User Accounts",
                    "Advanced Reporting",
                    "Priority Support",
                    "Ideal for small teams that require collaboration and additional features.",
                ],
            },
  
        ],
    };

    const rentalPricing = {
        "Single User": { price: 3000, features: ["1 User Account", "Monthly Billing", "Basic Support"] },
        "Dual User": { price: 6000, features: ["2 User Accounts", "Monthly Billing", "Priority Support"] },
        "Multi User": { price: 9000, features: ["Unlimited Users", "Monthly Billing", "Dedicated Manager"] },
    };

    const calculateGST = (price) => price * 0.18;
    const formatPrice = (price) =>
        selectedRegion === "India" ? `₹${price.toLocaleString()}` : `$${price}`;
    const getRentalPrice = () => rentalPricing[rentalPlan].price * rentalMonths;

    return (
        <div>
            <section className="pricing-section">
                <div className="pricing-content">
                    <h2 className="title">Find a Suitable Plan</h2>
                    <p className="subtitle">
                        Simple, transparent pricing that scales with your usage and needs
                    </p>

                    {/* Region Tabs */}
                    <div className="tabs">
                        {pricingData.regions.map((region) => (
                            <button
                                key={region}
                                className={`tab ${selectedRegion === region ? "active" : ""}`}
                                onClick={() => setSelectedRegion(region)}
                            >
                                {region}
                            </button>
                        ))}
                    </div>

                    {/* Pricing Boxes */}
                    <div className="pricing-boxes">
                        {pricingData.plans.map((plan, index) => {
                            const isSingleUser = plan.name === "Single User";
                            let displayPrice;

                            // Handle region-based pricing and discount
                            const regionPrice =
                                selectedRegion === "India"
                                    ? plan.indiaPrice
                                    : selectedRegion === "South Asia"
                                        ? plan.southAsiaPrice
                                        : plan.globalPrice;

                            if (isSingleUser && plan.discount && selectedRegion === "India") {
                                displayPrice = (
                                    <>
                                        {formatPrice(regionPrice)}
                                        <span className="gst-info">
                                            + 18% GST ({formatPrice(calculateGST(regionPrice))})
                                        </span>
                                    </>
                                );
                            } else {
                                displayPrice = (
                                    <>
                                        {formatPrice(regionPrice)}
                                        <span className="gst-info">
                                            + 18% GST ({formatPrice(calculateGST(regionPrice))})
                                        </span>
                                    </>
                                );
                            }

                            return (
                                <div
                                    key={index}
                                    className={`pricing-box ${plan.popular ? "popular" : ""}`}
                                >
                                    {/* Folded Ribbon for Single User */}
                                           

                                    {/* Fancy “Most Popular” badge for Multi User */}
                                    {plan.popular && (
                                        <span className="popular-badge-fancy"> Most Popular </span>
                                    )}

                                    <h3 className="plan-name">
                                        {plan.icon} {plan.name}
                                    </h3>
                                    <p className="plan-price">{displayPrice}</p>
                                 
                                    <p className="price-note">
                                        {selectedRegion === "India"
                                            ? "Perpetual License"
                                            : "Yearly Subscription"}
                                    </p>
                                    {/* {isSingleUser && selectedRegion === "India" && (
                                        <div className="ribbon-folded-left">🌟 20% Offer Limited Time 🌟</div>
                                    )} */}
                                    <div className="plan-features">
                                        <ul>
                                            {plan.features.map((f, i) => (
                                                <li key={i}>{f}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    <a href="/Demo">
                                        <button>Buy Now</button>
                                    </a>
                                </div>
                            );
                        })}

                        {/* Rental Plan (only India) */}
                        {selectedRegion === "India" && (
                            <div className="pricing-box rental bright">
                                <h3 className="plan-name">Rental Plan</h3>

                                <select
                                    value={rentalPlan}
                                    onChange={(e) => setRentalPlan(e.target.value)}
                                    className="rental-dropdown"
                                >
                                    {Object.keys(rentalPricing).map((plan) => (
                                        <option key={plan} value={plan}>
                                            {plan}
                                        </option>
                                    ))}
                                </select>

                                <div className="rental-months">
                                    {[1, 3, 12].map((m) => (
                                        <label key={m}>
                                            <input
                                                type="radio"
                                                name="months"
                                                value={m}
                                                checked={rentalMonths === m}
                                                onChange={() => setRentalMonths(m)}
                                            />{" "}
                                            {m} month{m > 1 ? "s" : ""}
                                        </label>
                                    ))}
                                </div>

                                <p className="plan-price">
                                    {formatPrice(getRentalPrice())}
                                    <span className="gst-info">
                                        + 18% GST ({formatPrice(calculateGST(getRentalPrice()))})
                                    </span>
                                </p>
                                <p className="price-note">Rental Subscription</p>

                                <div className="plan-features">
                                    <ul>
                                        {rentalPricing[rentalPlan].features.map((f, i) => (
                                            <li key={i}>{f}</li>
                                        ))}
                                    </ul>
                                </div>

                                <a href="/Demo">
                                    <button>Buy Now</button>
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </section>
            <Trial />
        </div>
    );
}
